// Незаписаното на работен лист — общо за доставка, изписване, прехвърляне и
// инвентаризация: флагът „незапазени промени" (`dirtyRef`, вдиган само от
// `touch()`; зареждането от редакция/чернова НЕ минава през него) и — при НОВ
// документ — черновата (`useDraftSync`) с действията на футъра: „Запази
// временно" (веднага на сървъра и обратно на началния екран), „Изтрий
// черновата" (след въпроса) и изчезването ѝ след успешен запис; с `resume` —
// и черновата при отваряне (`useDraftResume`: зарежда я или пита). При
// редакция остава само флагът.
//
// Снимката и `resume.apply` се четат лениво — листът може да подаде функции,
// които ползват редовете от hook-ове по-долу в тялото си.
import { useCallback, useRef } from "react";
import { App } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { DraftType } from "../../../../api/inventoryDrafts";
import type { DraftPayloadBase, DraftRouterState, LocalDraftStore } from "./draftModel";
import { useDraftResume } from "./useDraftResume";
import { useDraftSync, type DraftSnapshot } from "./useDraftSync";

type Params<P> = {
  type: DraftType;
  mode: "new" | "edit";
  // началният екран на операцията — „Запази временно" и „Изтрий" водят там
  homePath: string;
  // `{draftId, ownDraft}` — сървърната чернова, от която листът е отворен
  routerState?: DraftRouterState | null;
  local: LocalDraftStore<P>;
  snapshot: () => DraftSnapshot<P>;
  // черновата при отваряне на нов лист; без него листът не я търси (изписването
  // я решава на страницата си, преди да монтира листа)
  resume?: {
    // по подразбиране — нов документ; доставката го спира при prefill
    enabled?: boolean;
    hasContent: (payload: P) => boolean;
    // зареди черновата в листа (без `touch`)
    apply: (payload: P) => void;
    onLoadError: () => void;
  };
};

const noContent = () => false;

export const useWorksheetDraft = <P extends DraftPayloadBase>({
  type,
  mode,
  homePath,
  routerState = null,
  local,
  snapshot,
  resume,
}: Params<P>) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const enabled = mode === "new";
  const dirtyRef = useRef(false);
  const sync = useDraftSync({
    type,
    enabled,
    initialDraftId: routerState?.draftId ?? null,
    own: routerState?.ownDraft ?? true,
    snapshot,
    local,
  });
  const scheduleRef = useRef(sync.schedule);
  scheduleRef.current = sync.schedule;

  // Всяка реална промяна: „незапазено" + черновата след паузата
  const touch = useCallback(() => {
    dirtyRef.current = true;
    scheduleRef.current();
  }, []);

  const resumed = useDraftResume<P>({
    type,
    enabled: !!resume && (resume.enabled ?? enabled),
    routerState,
    local,
    hasContent: resume?.hasContent ?? noContent,
    onApply: ({ payload, draftId }) => {
      resume?.apply(payload);
      // моята чернова от сървъра, намерена без id в router state
      if (draftId != null) sync.adopt(draftId);
    },
    onLoadError: () => resume?.onLoadError(),
  });

  const saveTemp = () => {
    void sync.flush().then(() => {
      dirtyRef.current = false;
      message.success(t("inventoryPage.savedTemp"));
      navigate(homePath);
    });
  };

  // след потвърждение (тече с въртящ се бутон в самия въпрос)
  const deleteDraft = async () => {
    await sync.remove();
    dirtyRef.current = false;
    navigate(homePath);
  };

  // Записан нов документ → черновата изчезва. Вика се ПРЕДИ навигацията:
  // демонтажът на листа иначе само освобождава заключването.
  const onSaved = () => {
    dirtyRef.current = false;
    if (enabled) void sync.remove();
  };

  return { enabled, dirtyRef, touch, sync, resume: resumed, saveTemp, deleteDraft, onSaved };
};

export type WorksheetDraft = ReturnType<typeof useWorksheetDraft>;
